import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  ExternalLink,
  Eye,
  ShoppingBag,
  FilePen,
  Globe,
  LayoutGrid,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { ApiError, type Site, type Template } from "../../lib/api";
import { useDeleteSite, useSites, useTemplates, useUpdateSite } from "../../lib/queries";
import { siteCover } from "../../lib/template-art";
import { CATEGORIES } from "../../lib/taxonomy";
import { useAuthStore } from "../../stores/auth";
import { AppLayout } from "../../components/layout/AppLayout";
import { BrowserBar, Cover, TemplateCard } from "../../components/templates/TemplateCard";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Dialog } from "../../components/ui/Dialog";
import { EmptyState } from "../../components/ui/EmptyState";
import { SkeletonCard } from "../../components/ui/Skeleton";

const MAX_SITES = 3;
const FEATURED_TEMPLATES = 3;

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

/**
 * Dashboard (PRD §12): branded welcome header with site usage, stat tiles,
 * site cards (status, last edited, URL, edit/preview/rename/delete) capped at
 * 3 active sites per tenant, and featured templates to start from.
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data: sites, isPending, error, refetch } = useSites();
  const { data: templates } = useTemplates();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const count = sites?.length ?? 0;
  const published = sites?.filter((s) => s.status === "published").length ?? 0;
  const limitReached = count >= MAX_SITES;
  const deletingSite = sites?.find((s) => s.id === deletingId);
  const templatesById = useMemo(
    () => new Map<string, Template>((templates ?? []).map((t) => [t.id, t])),
    [templates],
  );
  const firstName = user?.email?.split("@")[0];

  function startRename(site: Site) {
    setRenameError(null);
    setRenamingId(site.id);
    setRenameValue(site.name);
  }

  async function submitRename(event: FormEvent, siteId: string) {
    event.preventDefault();
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    try {
      await updateSite.mutateAsync({ siteId, name: trimmed });
      setRenamingId(null);
    } catch (err) {
      setRenameError(
        err instanceof ApiError ? err.message : "Could not rename the site.",
      );
    }
  }

  return (
    <AppLayout>
      <main className="page page--wide">
        <section className="dash-hero">
          <div>
            <span className="eyebrow">
              {greeting()}
              {firstName ? `, ${firstName}` : ""}
            </span>
            <h1>Your websites</h1>
            <p>
              {count === 0
                ? "Create your first site from a template — pick a design, add your details, and publish."
                : "Edit, preview and publish your sites. Changes save automatically."}
            </p>
          </div>
          <div className="dash-hero__aside">
            <Button
              variant="primary"
              size="lg"
              disabled={limitReached}
              onClick={() => navigate("/app/onboarding")}
            >
              <Plus size={18} aria-hidden="true" />
              {limitReached ? `Limit reached (${count}/${MAX_SITES})` : "New site"}
            </Button>
            <div className="usage">
              {count} of {MAX_SITES} sites used
              <div
                className="usage__bar"
                role="progressbar"
                aria-label="Sites used"
                aria-valuemin={0}
                aria-valuemax={MAX_SITES}
                aria-valuenow={count}
              >
                <div
                  className="usage__fill"
                  style={{ width: `${Math.min(100, (count / MAX_SITES) * 100)}%` }}
                />
              </div>
            </div>
            {limitReached ? (
              <p className="limit-note">
                Your workspace is limited to 3 sites. Delete one to create another.
              </p>
            ) : null}
          </div>
        </section>

        {!isPending && !error && count > 0 ? (
          <div className="stat-row">
            <div className="stat-tile">
              <span className="stat-tile__icon" aria-hidden="true">
                <LayoutGrid size={20} />
              </span>
              <div>
                <div className="stat-tile__value">{count}</div>
                <div className="stat-tile__label">Total sites</div>
              </div>
            </div>
            <div className="stat-tile">
              <span className="stat-tile__icon stat-tile__icon--success" aria-hidden="true">
                <CheckCircle2 size={20} />
              </span>
              <div>
                <div className="stat-tile__value">{published}</div>
                <div className="stat-tile__label">Published</div>
              </div>
            </div>
            <div className="stat-tile">
              <span className="stat-tile__icon stat-tile__icon--muted" aria-hidden="true">
                <FilePen size={20} />
              </span>
              <div>
                <div className="stat-tile__value">{count - published}</div>
                <div className="stat-tile__label">Drafts</div>
              </div>
            </div>
          </div>
        ) : null}

        {isPending ? (
          <div className="site-grid" style={{ marginTop: "var(--z-space-8)" }}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : null}

        {error instanceof ApiError && error.status === 401 ? (
          <div style={{ marginTop: "var(--z-space-8)" }}>
            <EmptyState
              icon={<AlertCircle size={28} />}
              title="You're signed out"
              description="Log in to see and edit your websites."
              action={
                <Link className="btn btn--primary" to="/login" state={{ from: "/app/dashboard" }}>
                  Log in
                </Link>
              }
            />
          </div>
        ) : error ? (
          <div style={{ marginTop: "var(--z-space-8)" }}>
            <EmptyState
              icon={<AlertCircle size={28} />}
              title="Could not load your websites"
              description="Something went wrong. Please try again."
              action={
                <Button variant="secondary" onClick={() => refetch()}>
                  <RefreshCw size={16} />
                  Try again
                </Button>
              }
            />
          </div>
        ) : null}

        {!isPending && !error && count === 0 ? (
          <section className="dash-empty" style={{ marginTop: "var(--z-space-8)" }}>
            <div>
              <span className="eyebrow">Get started</span>
              <h2>No sites yet</h2>
              <p>Your professional website is three steps away.</p>
              <ol className="dash-empty__steps">
                <li>
                  <CheckCircle2 size={18} aria-hidden="true" />
                  Tell us what you do
                </li>
                <li>
                  <CheckCircle2 size={18} aria-hidden="true" />
                  Choose a designer-made template
                </li>
                <li>
                  <CheckCircle2 size={18} aria-hidden="true" />
                  Add your details and publish
                </li>
              </ol>
              <Link className="btn btn--primary btn--lg" to="/app/onboarding">
                <Plus size={18} aria-hidden="true" />
                Create your first site
              </Link>
            </div>
            <div className="collage" aria-hidden="true">
              {CATEGORIES.slice(0, 3).map((category) => (
                <Cover key={category.slug} image={category.cover} width={400} height={300} />
              ))}
            </div>
          </section>
        ) : null}

        {count > 0 ? (
          <div className="section-head">
            <div>
              <h2>Sites</h2>
            </div>
          </div>
        ) : null}

        <ul className="site-grid">
          {(sites ?? []).map((site) => {
            const host = `${window.location.host}/s/${site.slug}`;
            const live = site.status === "published";
            const isStore = site.templateId ? templatesById.get(site.templateId)?.kind === "store" : false;
            return (
              <li key={site.id} className="site-card">
                <div className="site-card__media">
                  <BrowserBar url={site.name} />
                  <div style={{ height: "calc(100% - 28px)", overflow: "hidden" }}>
                    <Cover image={siteCover(site, templatesById)} width={640} height={340} />
                  </div>
                  <div className="site-card__status">
                    <Badge variant={live ? "success" : "default"} dot>
                      {live ? "Published" : "Draft"}
                    </Badge>
                  </div>
                </div>
                <div className="site-card__body">
                  <div className="site-card__title">
                    <h2>{site.name}</h2>
                  </div>
                  <p className="site-card__meta">
                    <Clock size={14} aria-hidden="true" />
                    <span>Edited {site.updatedAt.slice(0, 10)}</span>
                  </p>
                  <p
                    className="site-card__url"
                    title={live ? "View published website" : "Public URL after publishing"}
                  >
                    <Globe size={14} aria-hidden="true" />
                    {live ? (
                      <a href={`/s/${site.slug}`} target="_blank" rel="noopener noreferrer">
                        {host}
                      </a>
                    ) : (
                      <span>{host}</span>
                    )}
                  </p>

                  {renamingId === site.id ? (
                    <form
                      className="inline-form"
                      style={{ marginTop: "var(--z-space-3)" }}
                      onSubmit={(event) => submitRename(event, site.id)}
                    >
                      <label>
                        New name
                        <input
                          aria-label="New site name"
                          className="form-input"
                          value={renameValue}
                          maxLength={100}
                          autoFocus
                          onChange={(event) => {
                            setRenameError(null);
                            setRenameValue(event.target.value);
                          }}
                        />
                      </label>
                      {renameError ? (
                        <div className="form-error" role="alert">
                          {renameError}
                        </div>
                      ) : null}
                      <div className="site-card__actions">
                        <Button
                          type="submit"
                          variant="primary"
                          size="sm"
                          loading={updateSite.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setRenamingId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="site-card__actions">
                      <Link className="btn btn--primary btn--sm" to={`/app/sites/${site.id}/editor`}>
                        <Pencil size={14} aria-hidden="true" />
                        Edit
                      </Link>
                      {isStore ? (
                        <Link className="btn btn--secondary btn--sm" to={`/app/sites/${site.id}/store`}>
                          <ShoppingBag size={14} aria-hidden="true" />
                          Store
                        </Link>
                      ) : null}
                      <Link className="btn btn--secondary btn--sm" to={`/app/sites/${site.id}/preview`}>
                        <Eye size={14} aria-hidden="true" />
                        Preview
                      </Link>
                      {live ? (
                        <a
                          className="btn btn--ghost btn--sm"
                          href={`/s/${site.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open live website"
                        >
                          <ExternalLink size={14} aria-hidden="true" />
                          Live
                        </a>
                      ) : null}
                      <span className="spacer" />
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => startRename(site)}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--danger"
                        aria-label="Delete"
                        title="Delete site"
                        onClick={() => setDeletingId(site.id)}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {templates && templates.length > 0 && !limitReached ? (
          <section aria-labelledby="featured-templates">
            <div className="section-head">
              <div>
                <h2 id="featured-templates">Start from a template</h2>
                <p>Designer-made layouts for your profession.</p>
              </div>
              <Link className="section-head__link" to="/app/templates">
                Browse all templates
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
            <div className="tpl-grid">
              {templates.slice(0, FEATURED_TEMPLATES).map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  headingLevel={3}
                  previewHref={`/app/templates/${template.id}`}
                  useHref={`/app/onboarding?template=${template.id}`}
                />
              ))}
            </div>
          </section>
        ) : null}

        <Dialog
          open={Boolean(deletingId)}
          title="Delete website"
          onClose={() => setDeletingId(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setDeletingId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={deleteSite.isPending}
                onClick={() => {
                  if (deletingId) {
                    deleteSite.mutate(deletingId, {
                      onSuccess: () => setDeletingId(null),
                    });
                  }
                }}
              >
                Confirm delete
              </Button>
            </>
          }
        >
          <p>
            Are you sure you want to delete{" "}
            <strong>{deletingSite?.name ?? "this site"}</strong>? This action
            cannot be undone.
          </p>
        </Dialog>
      </main>
    </AppLayout>
  );
}
